import { defineStore } from "pinia";
import { usePlanJoinStore } from "./PlanJoinStore";
import type { PlanJoinStatus } from "./PlanJoinStore";
import type { PlanInfo } from "~/entities/PlanInfo";
import type { PlanDocument } from "~/entities/PlanDocument";
import { Payment } from "~/entities/Payment";

export const usePayment3DSStore = defineStore(
  "Payment3DSStore",
  () => {
    const planJoinStore = usePlanJoinStore();

    // 3dセキュア認証用のストア
    // リダイレクトするので、認証情報をsessionStorageに保持する
    const authenticationId = ref<string | undefined>(undefined);
    // paymentのexpireYearとexpireMonthだけ保持する
    const payment = ref<Payment | undefined>(undefined);

    // planJoinStoreのデータを保持する
    const isExist = ref(false);
    const plan = ref<PlanInfo | undefined>(undefined);
    const priceId = ref<string | undefined>(undefined);
    const documents = ref<PlanDocument[]>([]);
    const flow = ref<PlanJoinStatus[]>([]);
    const hasAgreedConditions = ref(false);
    const hasAgreedDocuments = ref(false);
    const sbPaymentId = ref<number | undefined>(undefined);
    const bankTransferId = ref<number | undefined>(undefined);
    const divideTimes = ref<number | undefined>(undefined);
    const creditResultId = ref<number | undefined>(undefined);
    const inputEmail = ref<string>("");

    const reset = () => {
      authenticationId.value = undefined;
      payment.value = undefined;
    };

    const set = (paymentValue: Payment, authenticationIdValue: string) => {
      payment.value = new Payment();
      payment.value.expireMonth = paymentValue.expireMonth;
      payment.value.expireYear = paymentValue.expireYear;
      authenticationId.value = authenticationIdValue;

      savePlanJoinStoreData();
    };

    const resetPlanJoinStoreData = () => {
      isExist.value = false;
      plan.value = undefined;
      priceId.value = undefined;
      documents.value = [];
      flow.value = [];
      hasAgreedConditions.value = false;
      hasAgreedDocuments.value = false;
      sbPaymentId.value = undefined;
      bankTransferId.value = undefined;
      divideTimes.value = undefined;
      creditResultId.value = undefined;
      inputEmail.value = "";
    };

    // actions
    const savePlanJoinStoreData = () => {
      plan.value = planJoinStore.plan;
      priceId.value = planJoinStore.priceId;
      documents.value = planJoinStore.documents;
      flow.value = planJoinStore.flow;
      hasAgreedConditions.value = planJoinStore.hasAgreedConditions;
      hasAgreedDocuments.value = planJoinStore.hasAgreedDocuments;
      sbPaymentId.value = planJoinStore.sbPaymentId;
      bankTransferId.value = planJoinStore.bankTransferId;
      divideTimes.value = planJoinStore.divideTimes;
      creditResultId.value = planJoinStore.creditResultId;
      inputEmail.value = planJoinStore.inputEmail;
      isExist.value = true;
    };

    const restorePlanJoinStoreData = (planId: string, planPriceId: string) => {
      if (!isExist.value) {
        return;
      }
      if (plan.value?.id !== planId || priceId.value !== planPriceId) {
        return;
      }
      planJoinStore.plan = plan.value;
      planJoinStore.priceId = priceId.value;
      planJoinStore.documents = documents.value;
      planJoinStore.flow = flow.value;
      planJoinStore.hasAgreedConditions = hasAgreedConditions.value;
      planJoinStore.hasAgreedDocuments = hasAgreedDocuments.value;
      planJoinStore.sbPaymentId = sbPaymentId.value;
      planJoinStore.bankTransferId = bankTransferId.value;
      planJoinStore.divideTimes = divideTimes.value;
      planJoinStore.creditResultId = creditResultId.value;
      planJoinStore.inputEmail = inputEmail.value;
      resetPlanJoinStoreData();
    };

    return {
      authenticationId,
      payment,
      // 実際には使わないがstorageに保存するため、変数もここに記載する
      isExist,
      plan,
      priceId,
      documents,
      flow,
      hasAgreedConditions,
      hasAgreedDocuments,
      sbPaymentId,
      bankTransferId,
      divideTimes,
      creditResultId,
      inputEmail,
      reset,
      set,
      savePlanJoinStoreData,
      restorePlanJoinStoreData,
    };
  },
  {
    persist: {
      storage: persistedState.sessionStorage,
    },
  },
);
